export function isiOS(): boolean {
  const userAgent = window.navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(userAgent) &&
    !(window as Window & { MSStream?: unknown }).MSStream
  );
}

function getOS(userAgent: string): string {
  if (/Windows NT 10/.test(userAgent)) return 'Windows 10';
  if (/Windows NT 6\.3/.test(userAgent)) return 'Windows 8.1';
  if (/Windows NT 6\.2/.test(userAgent)) return 'Windows 8';
  if (/Windows NT 6\.1/.test(userAgent)) return 'Windows 7';
  if (/Windows/.test(userAgent)) return 'Windows';
  if (/Mac OS X/.test(userAgent))
    return userAgent.match(/Mac OS X ([^)]+)/)?.[1]?.replace(/_/g, '.')
      ? `macOS ${userAgent.match(/Mac OS X ([^)]+)/)?.[1]?.replace(/_/g, '.')}`
      : 'macOS';
  if (/Android/.test(userAgent))
    return userAgent.match(/Android ([^;)]+)/)?.[1]
      ? `Android ${userAgent.match(/Android ([^;)]+)/)?.[1]}`
      : 'Android';
  if (/iPad|iPhone|iPod/.test(userAgent))
    return userAgent.match(/OS ([\d_]+)/)?.[1]
      ? `iOS ${userAgent.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.')}`
      : 'iOS';
  if (/Linux/.test(userAgent)) return 'Linux';
  return 'Unknown OS';
}

function getBrowser(userAgent: string): string {
  if (/Edg\//.test(userAgent))
    return `Edge ${userAgent.match(/Edg\/([\d.]+)/)?.[1] ?? ''}`.trim();
  if (/OPR\//.test(userAgent))
    return `Opera ${userAgent.match(/OPR\/([\d.]+)/)?.[1] ?? ''}`.trim();
  if (/Chrome\//.test(userAgent))
    return `Chrome ${userAgent.match(/Chrome\/([\d.]+)/)?.[1] ?? ''}`.trim();
  if (/Firefox\//.test(userAgent))
    return `Firefox ${userAgent.match(/Firefox\/([\d.]+)/)?.[1] ?? ''}`.trim();
  if (/Safari\//.test(userAgent))
    return `Safari ${userAgent.match(/Version\/([\d.]+)/)?.[1] ?? ''}`.trim();
  return 'Unknown Browser';
}

function getDeviceType(userAgent: string): string {
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'Tablet';
  if (
    /mobi|android|iphone|ipod|blackberry|opera mini|windows phone/i.test(
      userAgent,
    )
  )
    return 'Mobile';
  return 'Desktop';
}

type ExtendedNavigator = Navigator & {
  deviceMemory?: number;
  connection?: { effectiveType?: string };
};

export async function getDeviceDetails(): Promise<Record<string, string>> {
  const ua = navigator.userAgent;

  const extendedNavigator = navigator as ExtendedNavigator;

  const details: Record<string, string> = {
    userAgent: ua,
    os: getOS(ua),
    browser: getBrowser(ua),
    deviceType: getDeviceType(ua),
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: String(window.screen.colorDepth),
    hardwareConcurrency: String(navigator.hardwareConcurrency ?? ''),
    deviceMemory: String(extendedNavigator.deviceMemory ?? ''),
    connectionType: String(extendedNavigator.connection?.effectiveType ?? ''),
  };

  // Geolocation
  try {
    const position = await new Promise<GeolocationPosition>(
      (resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          maximumAge: 60000,
        });
      },
    );
    details.latitude = String(position.coords.latitude);
    details.longitude = String(position.coords.longitude);
    if (position.coords.accuracy) {
      details.locationAccuracy = `${position.coords.accuracy}m`;
    }
  } catch {
    // permission denied or unavailable
  }

  return details;
}
